import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  type Unsubscribe,
} from 'firebase/firestore'
import { z } from 'zod'
import { db } from './firebase'
import {
  snippetInputSchema,
  snippetSchema,
  snippetUpdateSchema,
} from './validation'
import type { Snippet, SnippetInput, SnippetUpdate } from '@/types'

const snippetFirestoreSchema = z.object({
  userId: z.string(),
  content: z.string(),
  createdAt: z.instanceof(Timestamp),
  updatedAt: z.instanceof(Timestamp),
  date: z.string(),
  isDeleted: z.boolean(),
})

// Convert Firestore document to Snippet
const docToSnippet = (id: string, data: unknown): Snippet | null => {
  const parsed = snippetFirestoreSchema.safeParse(data)
  if (!parsed.success) {
    return null
  }

  const candidate = {
    id,
    userId: parsed.data.userId,
    content: parsed.data.content,
    createdAt: parsed.data.createdAt.toDate(),
    updatedAt: parsed.data.updatedAt.toDate(),
    date: parsed.data.date,
    isDeleted: parsed.data.isDeleted,
  }

  const validated = snippetSchema.safeParse(candidate)
  if (!validated.success) {
    return null
  }

  return validated.data
}

const isSnippet = (snippet: Snippet | null): snippet is Snippet =>
  snippet !== null

// Create snippet
export const createSnippet = async (
  userId: string,
  input: SnippetInput
): Promise<string> => {
  const validated = snippetInputSchema.parse(input)

  const docRef = await addDoc(collection(db, 'snippets'), {
    userId,
    content: validated.content,
    date: validated.date,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    isDeleted: false,
  })

  return docRef.id
}

// Update snippet
export const updateSnippet = async (
  snippetId: string,
  update: SnippetUpdate
): Promise<void> => {
  const validated = snippetUpdateSchema.parse(update)

  await updateDoc(doc(db, 'snippets', snippetId), {
    content: validated.content,
    updatedAt: serverTimestamp(),
  })
}

// Soft delete snippet
export const deleteSnippet = async (snippetId: string): Promise<void> => {
  await updateDoc(doc(db, 'snippets', snippetId), {
    isDeleted: true,
    updatedAt: serverTimestamp(),
  })
}

// Hard delete snippet (for cleanup)
export const hardDeleteSnippet = async (snippetId: string): Promise<void> => {
  await deleteDoc(doc(db, 'snippets', snippetId))
}

// Subscribe to user's snippets for a specific date
export const subscribeToSnippetsByDate = (
  userId: string,
  date: string,
  callback: (snippets: Snippet[]) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  const q = query(
    collection(db, 'snippets'),
    where('userId', '==', userId),
    where('date', '==', date),
    where('isDeleted', '==', false),
    orderBy('createdAt', 'desc')
  )

  return onSnapshot(
    q,
    (snapshot) => {
      const snippets = snapshot.docs
        .map((doc) => docToSnippet(doc.id, doc.data()))
        .filter(isSnippet)
      callback(snippets)
    },
    onError
  )
}

// Subscribe to user's all snippets (paginated in future)
export const subscribeToUserSnippets = (
  userId: string,
  callback: (snippets: Snippet[]) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  const q = query(
    collection(db, 'snippets'),
    where('userId', '==', userId),
    where('isDeleted', '==', false),
    orderBy('date', 'desc'),
    orderBy('createdAt', 'desc')
  )

  return onSnapshot(
    q,
    (snapshot) => {
      const snippets = snapshot.docs
        .map((doc) => docToSnippet(doc.id, doc.data()))
        .filter(isSnippet)
      callback(snippets)
    },
    onError
  )
}
